import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import React, { useState } from 'react';
import { Car, MapPin, Star, Shield, Clock, ArrowRight, ChevronRight, Plane, Wine, Sun, Waves, Mountain, Heart, Map, Users, Phone, Mail, Calendar, Check, Menu, X } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../components/LanguageSwitcher';
import GoogleReviews from '../components/GoogleReviews';

export default function LandingPage() {
  const { t } = useTranslation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  const VEHICLES = [
    { 
      type: 'van', 
      name: 'Van', 
      passengers: '1-8', 
      price: 38, 
      icon: '🚐', 
      desc: 'Nissan NV200 & Mercedes Vito',
      image: 'https://customer-assets.emergentagent.com/job_ada0d8bc-4b70-4972-86b9-0427b9931920/artifacts/xmei7n80_image00002%20%281%29.jpeg'
    },
  ];

  const activities = [
    { name: 'Airport Transfers', icon: <Plane className="w-6 h-6" />, desc: 'Comfortable pickup from Santorini Airport or Port' },
    { name: 'Wine Tours', icon: <Wine className="w-6 h-6" />, desc: 'Visit award-winning wineries with caldera views' },
    { name: 'Sunset Experience', icon: <Sun className="w-6 h-6" />, desc: 'Witness the famous Oia sunset' },
    { name: 'Beach Tours', icon: <Waves className="w-6 h-6" />, desc: 'Red Beach, Black Beach & Perissa' },
    { name: 'Caldera Tours', icon: <Mountain className="w-6 h-6" />, desc: 'Full volcanic island exploration' },
    { name: 'Wedding Transport', icon: <Heart className="w-6 h-6" />, desc: 'Elegant cars for your special day' },
  ];

  return (
    <>
      <Helmet>
        <title>Santorini Airport Transfer | Luxury Tours & Private Transfers | Santorini Best Experience</title>
        <meta name="description" content="Premium Santorini airport transfers & private tours in luxury vans. Flat rate €38 (1-4 pax) or €50 (5-8 pax) anywhere in Santorini. Book online 24/7. JTR Airport, Athinios Port, wine tours, sunset experiences." />
        <meta name="keywords" content="Santorini transfer, Santorini airport transfer, Santorini private tour, luxury transfer Santorini, Santorini taxi, JTR airport transfer, Oia transfer, Fira transfer, Santorini wine tour, private driver Santorini, Mercedes transfer Santorini, VIP transfer Santorini, Athinios port transfer, Santorini sunset tour, wedding transport Santorini" />
        <link rel="canonical" href="https://santorinibestexperience.com" />
        
        {/* Open Graph */}
        <meta property="og:title" content="Santorini Airport Transfer | Luxury Tours & Private Transfers" />
        <meta property="og:description" content="Premium Santorini airport transfers & private tours in luxury vans. Flat rate from €38. Book online 24/7." />
        <meta property="og:url" content="https://santorinibestexperience.com" />
        <meta property="og:type" content="website" />
        
        {/* Twitter */}
        <meta name="twitter:title" content="Santorini Airport Transfer | Luxury Tours & Private Transfers" />
        <meta name="twitter:description" content="Premium Santorini airport transfers & private tours in luxury vans. Flat rate from €38. Book online 24/7." />
      </Helmet>
      
      <div className="min-h-screen bg-[#FAFAF7] pb-16 md:pb-0">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#FAFAF7]/80 backdrop-blur-lg border-b border-slate-200/50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_santorini-booking/artifacts/b01x660c_FullLogo%20%281%29.png" 
                alt="Santorini Best Experience"
                className="h-12 md:h-14"
              />
              <div className="hidden sm:block">
                <h1 className="text-xl md:text-2xl font-bold text-slate-900 leading-tight">Santorini Best</h1>
                <p className="text-[#B8860B] text-sm md:text-base font-semibold">Experience</p>
              </div>
            </div>
            
            <div className="hidden md:flex items-center gap-8">
              <a href="#services" className="text-slate-600 hover:text-slate-900 transition-colors">{t('nav.services')}</a>
              <a href="#vehicles" className="text-slate-600 hover:text-slate-900 transition-colors">{t('nav.fleet')}</a>
              <a href="#activities" className="text-slate-600 hover:text-slate-900 transition-colors">{t('nav.tours')}</a>
              <Link to="/about" className="text-slate-600 hover:text-slate-900 transition-colors" data-testid="nav-about-link">About</Link>
              <a href="#contact" className="text-slate-600 hover:text-slate-900 transition-colors">{t('nav.contact')}</a>
            </div>
            
            <div className="flex items-center gap-2 md:gap-4">
              <LanguageSwitcher />
              <Link to="/" className="hidden md:inline-flex">
                <Button variant="ghost" className="text-slate-700 hover:text-slate-900">
                  {t('nav.home')}
                </Button>
              </Link>
              <Link to="/book">
                <Button className="bg-[#0A2540] hover:bg-[#153554] text-white font-medium tracking-wider uppercase text-xs px-4 md:px-6 h-10 rounded-full">
                  {t('nav.bookNow')}
                </Button>
              </Link>
              {/* Mobile hamburger button */}
              <button
                type="button"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="md:hidden flex items-center justify-center w-10 h-10 rounded-full bg-white border border-slate-200 text-slate-900 hover:bg-slate-100 transition-colors"
                aria-label="Menu"
                data-testid="mobile-menu-toggle"
              >
                {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
            </div>
          </div>

          {/* Mobile menu dropdown */}
          {mobileMenuOpen && (
            <div className="md:hidden mt-4 pb-4 border-t border-slate-200 pt-4 space-y-1" data-testid="mobile-menu">
              <a
                href="#services"
                onClick={() => setMobileMenuOpen(false)}
                className="block px-4 py-3 rounded-xl text-slate-700 hover:bg-slate-100 hover:text-slate-900 font-medium"
              >
                {t('nav.services')}
              </a>
              <a
                href="#vehicles"
                onClick={() => setMobileMenuOpen(false)}
                className="block px-4 py-3 rounded-xl text-slate-700 hover:bg-slate-100 hover:text-slate-900 font-medium"
              >
                {t('nav.fleet')}
              </a>
              <a
                href="#activities"
                onClick={() => setMobileMenuOpen(false)}
                className="block px-4 py-3 rounded-xl text-slate-700 hover:bg-slate-100 hover:text-slate-900 font-medium"
              >
                {t('nav.tours')}
              </a>
              <Link
                to="/about"
                onClick={() => setMobileMenuOpen(false)}
                className="block px-4 py-3 rounded-xl text-slate-700 hover:bg-slate-100 hover:text-slate-900 font-medium"
                data-testid="mobile-nav-about"
              >
                About
              </Link>
              <a
                href="#contact"
                onClick={() => setMobileMenuOpen(false)}
                className="block px-4 py-3 rounded-xl text-slate-700 hover:bg-slate-100 hover:text-slate-900 font-medium"
              >
                {t('nav.contact')}
              </a>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-6 overflow-hidden min-h-screen flex items-center">
        {/* Background Image - Santorini clearly visible */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1533105079780-92b9be482077?w=2400&q=95&auto=format&fit=crop')`,
          }}
        />
        {/* Very light overlay only where text sits — image stays sharp */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#FAFAF7]/85 via-[#FAFAF7]/40 to-transparent" />
        {/* Soft bottom fade for smooth transition */}
        <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-b from-transparent to-[#FAFAF7]" />
        
        <div className="max-w-7xl mx-auto relative z-10 w-full">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="animate-fade-in">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#B8860B]/10 border border-[#0A2540]/20 text-[#B8860B] text-sm font-medium mb-6">
                <Sun className="w-4 h-4" />
                {t('hero.badge')}
              </div>
              
              <h1 className="text-5xl md:text-7xl font-bold text-slate-900 mb-6 leading-tight">
                {t('hero.title1')}<br />
                <span className="bg-[#0A2540] bg-clip-text text-transparent">{t('hero.title2')}</span>
              </h1>
              
              <p className="text-xl text-slate-700 mb-8 max-w-lg">
                {t('hero.subtitle')}
              </p>
              
              {/* Discount Banners */}
              <div className="grid sm:grid-cols-2 gap-3 mb-8 max-w-lg">
                <div className="bg-gradient-to-r from-green-500/20 to-emerald-500/20 border border-green-500/40 rounded-full p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <Check className="w-5 h-5 text-green-400" />
                    <span className="text-slate-900 font-bold">€38 Flat Rate</span>
                  </div>
                  <p className="text-sm text-slate-700">1-4 pax anywhere in Santorini</p>
                </div>
                
                <div className="bg-gradient-to-r from-amber-500/20 to-orange-500/20 border border-[#0A2540]/40 rounded-full p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <Calendar className="w-5 h-5 text-[#B8860B]" />
                    <span className="text-slate-900 font-bold">Save 10%</span>
                  </div>
                  <p className="text-sm text-slate-700">Book 30+ days in advance</p>
                </div>
                
                <div className="bg-gradient-to-r from-cyan-500/20 to-blue-500/20 border border-cyan-500/40 rounded-full p-4 sm:col-span-2">
                  <div className="flex items-center gap-2 mb-1">
                    <ArrowRight className="w-5 h-5 text-cyan-400 rotate-180" />
                    <ArrowRight className="w-5 h-5 text-cyan-400" />
                    <span className="text-slate-900 font-bold">Round-Trip Discount</span>
                  </div>
                  <p className="text-sm text-slate-700">Book arrival + departure together and save 10%</p>
                </div>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/book">
                  <Button 
                    className="h-14 px-14 bg-[#0A2540] hover:bg-[#153554] text-white font-medium text-sm tracking-[0.2em] uppercase rounded-full"
                    data-testid="hero-book-btn"
                  >
                    {t('hero.bookTransfer')}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
                <a href="#activities">
                  <Button 
                    variant="outline" 
                    className="h-14 px-14 border-slate-300 text-slate-900 hover:bg-slate-100 text-sm font-medium tracking-[0.2em] uppercase rounded-full bg-white"
                  >
                    {t('hero.viewTours')}
                  </Button>
                </a>
              </div>
              
              <div className="flex items-center gap-8 mt-12 pt-8 border-t border-slate-200">
                <div>
                  <p className="text-3xl font-bold text-slate-900">4.9</p>
                  <div className="flex items-center gap-1 mt-1">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 fill-amber-400 text-[#B8860B]" />
                    ))}
                  </div>
                  <p className="text-sm text-slate-500 mt-1">{t('hero.guestRating')}</p>
                </div>
                <div className="w-px h-16 bg-slate-100" />
                <div>
                  <p className="text-3xl font-bold text-slate-900">5000+</p>
                  <p className="text-sm text-slate-500 mt-1">{t('hero.happyGuests')}</p>
                </div>
                <div className="w-px h-16 bg-slate-100" />
                <div>
                  <p className="text-3xl font-bold text-slate-900">10+</p>
                  <p className="text-sm text-slate-500 mt-1">{t('hero.yearsExperience')}</p>
                </div>
              </div>
            </div>
            
            <div className="relative hidden lg:block">
              <div className="relative z-10 bg-white/90 backdrop-blur-sm border border-slate-200 rounded-3xl p-6 shadow-2xl">
                <h3 className="text-xl font-bold text-slate-900 mb-4">{t('quickBooking.title')}</h3>
                
                <div className="space-y-4 mb-6">
                  <Link to="/book?type=transfer&pickup=airport">
                    <div className="bg-slate-100 rounded-full p-4 hover:bg-slate-200 transition-colors cursor-pointer relative overflow-hidden">
                      <div className="absolute top-0 right-0 bg-gradient-to-r from-green-500 to-emerald-500 text-slate-900 text-[10px] font-bold px-3 py-1 rounded-bl-xl">
                        UP TO 20% OFF
                      </div>
                      <p className="text-xs text-slate-500 mb-1">{t('quickBooking.popular')}</p>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Plane className="w-5 h-5 text-[#B8860B]" />
                          <span className="text-slate-900">{t('quickBooking.airportTransfer')}</span>
                        </div>
                        <div className="text-right">
                          <span className="text-[#B8860B] font-semibold block">from €38</span>
                          <span className="text-[10px] text-green-400">Early booking + round-trip!</span>
                        </div>
                      </div>
                    </div>
                  </Link>
                  
                  <Link to="/book?type=transfer&pickup=port">
                    <div className="bg-slate-100 rounded-full p-4 hover:bg-slate-200 transition-colors cursor-pointer">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <MapPin className="w-5 h-5 text-[#B8860B]" />
                          <span className="text-slate-900">{t('quickBooking.portTransfer')}</span>
                        </div>
                        <span className="text-[#B8860B] font-semibold">from €38</span>
                      </div>
                    </div>
                  </Link>
                  
                  <Link to="/book?type=tour">
                    <div className="bg-slate-100 rounded-full p-4 hover:bg-slate-200 transition-colors cursor-pointer">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Wine className="w-5 h-5 text-[#B8860B]" />
                          <span className="text-slate-900">{t('quickBooking.wineTour')} (4h)</span>
                        </div>
                        <span className="text-[#B8860B] font-semibold">{t('quickBooking.from')} €260</span>
                      </div>
                    </div>
                  </Link>
                  
                  <Link to="/book?type=tour">
                    <div className="bg-slate-100 rounded-full p-4 hover:bg-slate-200 transition-colors cursor-pointer">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Sun className="w-5 h-5 text-[#B8860B]" />
                          <span className="text-slate-900">{t('quickBooking.sunsetExperience')} (3h)</span>
                        </div>
                        <span className="text-[#B8860B] font-semibold">{t('quickBooking.from')} €195</span>
                      </div>
                    </div>
                  </Link>

                  <Link to="/book?type=tour">
                    <div className="bg-[#0A2540]/5 rounded-full p-4 hover:bg-[#0A2540]/10 transition-colors cursor-pointer border border-[#0A2540]/20" data-testid="quick-book-cruise-tour">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Waves className="w-5 h-5 text-[#0A2540]" />
                          <span className="text-slate-900">Cruise → Oia (3h)</span>
                        </div>
                        <span className="text-[#B8860B] font-semibold">from €200</span>
                      </div>
                    </div>
                  </Link>
                </div>
                
                <Link to="/book">
                  <Button className="w-full h-12 bg-[#0A2540] hover:bg-[#153554] text-white font-semibold rounded-full">
                    {t('nav.bookNow')}
                  </Button>
                </Link>
              </div>
              
              <div className="absolute -top-10 -right-10 w-64 h-64 bg-[#B8860B]/20 rounded-full blur-3xl" />
              <div className="absolute -bottom-10 -left-10 w-48 h-48 bg-orange-500/10 rounded-full blur-2xl" />
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Our Services</h2>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">
              Professional transportation services tailored to your Santorini experience
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: <Plane className="w-8 h-8" />,
                title: "Airport & Port Transfers",
                description: "Reliable pickup and drop-off from Santorini Airport (JTR) and Athinios Port. Meet & greet service included."
              },
              {
                icon: <Map className="w-8 h-8" />,
                title: "Private Tours",
                description: "Customized island tours with local expert drivers. Wine tours, sunset trips, beach excursions & more."
              },
              {
                icon: <Clock className="w-8 h-8" />,
                title: "Hourly Hire",
                description: "Flexible hourly booking for exploring at your own pace. Perfect for photographers and couples."
              },
              {
                icon: <Heart className="w-8 h-8" />,
                title: "Wedding Transport",
                description: "Elegant transportation for your special day. Decorated vehicles and professional chauffeurs."
              },
              {
                icon: <Users className="w-8 h-8" />,
                title: "Group Transport",
                description: "Van for larger groups up to 8 passengers. Ideal for families, friends, and corporate events."
              },
              {
                icon: <Shield className="w-8 h-8" />,
                title: "VIP Service",
                description: "Premium vehicles with luxury amenities. Champagne, refreshments & personalized service."
              }
            ].map((service, index) => (
              <div 
                key={index}
                className="bg-white border border-slate-200 rounded-2xl p-8 hover:border-[#0A2540]/30 transition-all duration-300 group"
              >
                <div className="w-14 h-14 rounded-full bg-[#B8860B]/10 flex items-center justify-center text-[#B8860B] mb-6 group-hover:bg-[#B8860B]/20 transition-colors">
                  {service.icon}
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-3">{service.title}</h3>
                <p className="text-slate-600">{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Vehicles Section */}
      <section id="vehicles" className="py-20 px-6 bg-white/50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Our Fleet</h2>
            <p className="text-xl text-slate-600">Premium Mercedes vehicles for every occasion</p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {VEHICLES.map((vehicle, index) => (
              <div key={index} className="bg-white border border-slate-200 rounded-2xl overflow-hidden hover:border-[#0A2540]/30 transition-all group">
                {/* Vehicle Image */}
                <div className="relative h-48 overflow-hidden">
                  <img 
                    src={vehicle.image} 
                    alt={vehicle.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-white via-transparent to-transparent"></div>
                  <div className="absolute top-4 left-4 text-4xl">{vehicle.icon}</div>
                </div>
                
                {/* Vehicle Details */}
                <div className="p-6">
                  <h3 className="text-xl font-bold text-slate-900 mb-2">{vehicle.name}</h3>
                  <p className="text-slate-500 text-sm mb-4">{vehicle.desc}</p>
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2 text-slate-600">
                      <Users className="w-4 h-4" />
                      <span>Up to {vehicle.passengers}</span>
                    </div>
                  </div>
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <div>
                      <span className="text-2xl font-bold text-[#B8860B]">€{vehicle.price}</span>
                      <span className="text-slate-500 text-sm ml-1">flat / transfer</span>
                    </div>
                    <span className="text-slate-600">•</span>
                    <div>
                      <span className="text-lg font-semibold text-slate-900">€65</span>
                      <span className="text-slate-500 text-sm ml-1">/hour tour</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Tours & Activities Section */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
              Discover Santorini
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Explore the island's most breathtaking destinations with our curated tours and experiences
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Sunset Tour */}
            <div className="bg-white rounded-3xl overflow-hidden border border-slate-200 hover:border-[#0A2540]/50 transition-all group">
              <div className="relative h-64 overflow-hidden">
                <img 
                  src="https://images.unsplash.com/photo-1672622851784-0dbd3df4c088?crop=entropy&cs=srgb&fm=jpg&q=85" 
                  alt="Santorini Sunset in Oia"
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-white via-white/50 to-transparent"></div>
                <div className="absolute bottom-4 left-4">
                  <span className="px-3 py-1 bg-[#B8860B] text-slate-900 text-xs font-semibold rounded-full">
                    Most Popular
                  </span>
                </div>
              </div>
              <div className="p-6">
                <h3 className="text-2xl font-bold text-slate-900 mb-3">Sunset Experience in Oia</h3>
                <p className="text-slate-600 mb-4 leading-relaxed">
                  Witness the world-famous Santorini sunset from Oia's iconic blue-domed churches. Enjoy the magical golden hour as the sun dips into the Aegean Sea, painting the white buildings in warm hues.
                </p>
                <div className="space-y-2 mb-6">
                  <div className="flex items-center gap-2 text-slate-600 text-sm">
                    <MapPin className="w-4 h-4 text-[#B8860B]" />
                    <span>Oia Castle • Blue Domes • Three Bells Church</span>
                  </div>
                  <div className="flex items-center gap-2 text-slate-600 text-sm">
                    <Clock className="w-4 h-4 text-[#B8860B]" />
                    <span>3 hours • Best at 6-8 PM</span>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-[#B8860B] font-bold text-xl">from €195</span>
                  <Link to="/book?type=tour">
                    <Button className="bg-[#B8860B] hover:bg-amber-600 text-slate-900">
                      Book Tour
                    </Button>
                  </Link>
                </div>
              </div>
            </div>

            {/* Wine Tour */}
            <div className="bg-white rounded-3xl overflow-hidden border border-slate-200 hover:border-[#0A2540]/50 transition-all group">
              <div className="relative h-64 overflow-hidden">
                <img 
                  src="https://images.unsplash.com/photo-1565733980542-e44b92a143c9?crop=entropy&cs=srgb&fm=jpg&q=85" 
                  alt="Santorini Vineyard Tour"
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-white via-white/50 to-transparent"></div>
              </div>
              <div className="p-6">
                <h3 className="text-2xl font-bold text-slate-900 mb-3">Wine Tour & Tasting</h3>
                <p className="text-slate-600 mb-4 leading-relaxed">
                  Visit traditional wineries and taste Santorini's unique volcanic wines. Learn about the ancient Assyrtiko grape and "kouloura" vine basket technique while enjoying stunning caldera views.
                </p>
                <div className="space-y-2 mb-6">
                  <div className="flex items-center gap-2 text-slate-600 text-sm">
                    <MapPin className="w-4 h-4 text-[#B8860B]" />
                    <span>Santo Wines • Estate Argyros • Venetsanos</span>
                  </div>
                  <div className="flex items-center gap-2 text-slate-600 text-sm">
                    <Clock className="w-4 h-4 text-[#B8860B]" />
                    <span>4 hours • 3-4 wineries</span>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-[#B8860B] font-bold text-xl">from €260</span>
                  <Link to="/book?type=tour">
                    <Button className="bg-[#B8860B] hover:bg-amber-600 text-slate-900">
                      Book Tour
                    </Button>
                  </Link>
                </div>
              </div>
            </div>

            {/* Cruise Passenger Tour */}
            <div className="bg-white rounded-3xl overflow-hidden border-2 border-[#0A2540] hover:border-[#B8860B] transition-all group relative" data-testid="cruise-tour-card">
              <div className="absolute top-4 right-4 z-10 px-3 py-1 rounded-full bg-[#0A2540] text-white text-xs font-bold uppercase tracking-wider">
                Cruise Special
              </div>
              <div className="relative h-64 overflow-hidden">
                <img 
                  src="https://images.unsplash.com/photo-1613395877344-13d4a8e0d49e?crop=entropy&cs=srgb&fm=jpg&q=85" 
                  alt="Cruise to Oia tour"
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-white via-white/50 to-transparent"></div>
              </div>
              <div className="p-6">
                <h3 className="text-2xl font-bold text-slate-900 mb-3">Cruise → Oia Highlights</h3>
                <p className="text-slate-600 mb-4 leading-relaxed">
                  Perfect for cruise passengers with limited time ashore. Pick-up from the cruise port, private van tour through Firostefani & Imerovigli's caldera views, and the iconic Oia — then straight back to your ship.
                </p>
                <div className="space-y-2 mb-6">
                  <div className="flex items-center gap-2 text-slate-600 text-sm">
                    <MapPin className="w-4 h-4 text-[#B8860B]" />
                    <span>Cruise Port → Firostefani → Imerovigli → Oia → Return</span>
                  </div>
                  <div className="flex items-center gap-2 text-slate-600 text-sm">
                    <Clock className="w-4 h-4 text-[#B8860B]" />
                    <span>3 hours • Private Van</span>
                  </div>
                  <div className="flex items-center gap-2 text-slate-600 text-sm">
                    <Users className="w-4 h-4 text-[#B8860B]" />
                    <span>Up to 4 pax: <strong className="text-slate-900">€200</strong> · Up to 6 pax: <strong className="text-slate-900">€250</strong></span>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-[#B8860B] font-bold text-xl">from €200</span>
                  <Link to="/book?type=tour">
                    <Button className="bg-[#0A2540] hover:bg-[#153554] text-white font-medium tracking-wider uppercase text-xs px-6 h-10 rounded-full">
                      Book Tour
                    </Button>
                  </Link>
                </div>
              </div>
            </div>

            {/* Beach Tour */}
            <div className="bg-white rounded-3xl overflow-hidden border border-slate-200 hover:border-[#0A2540]/50 transition-all group">
              <div className="relative h-64 overflow-hidden">
                <img 
                  src="https://images.unsplash.com/photo-1758829421372-e32405b6a707?crop=entropy&cs=srgb&fm=jpg&q=85" 
                  alt="Santorini Red Beach"
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-white via-white/50 to-transparent"></div>
              </div>
              <div className="p-6">
                <h3 className="text-2xl font-bold text-slate-900 mb-3">Beach & Volcano Tour</h3>
                <p className="text-slate-600 mb-4 leading-relaxed">
                  Explore Santorini's unique volcanic beaches with their distinctive red and black sands. Visit the famous Red Beach, Black Beach (Perissa), and White Beach, each showcasing the island's volcanic heritage.
                </p>
                <div className="space-y-2 mb-6">
                  <div className="flex items-center gap-2 text-slate-600 text-sm">
                    <MapPin className="w-4 h-4 text-[#B8860B]" />
                    <span>Red Beach • Perissa • Akrotiri • Kamari</span>
                  </div>
                  <div className="flex items-center gap-2 text-slate-600 text-sm">
                    <Clock className="w-4 h-4 text-[#B8860B]" />
                    <span>5 hours • Swimming included</span>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-[#B8860B] font-bold text-xl">from €275</span>
                  <Link to="/book?type=tour">
                    <Button className="bg-[#B8860B] hover:bg-amber-600 text-slate-900">
                      Book Tour
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>

          {/* Additional Quick Tours */}
          <div className="mt-12 grid md:grid-cols-2 gap-6">
            <div className="bg-white border border-slate-200 rounded-2xl p-6 hover:border-[#0A2540]/50 transition-all">
              <h4 className="text-xl font-bold text-slate-900 mb-2">Airport Transfer</h4>
              <p className="text-slate-600 text-sm mb-4">
                Professional transfer service to/from Santorini Airport (JTR). Comfortable vehicles, meet & greet service included.
              </p>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4 text-slate-600 text-sm">
                  <span className="flex items-center gap-1">
                    <Plane className="w-4 h-4" /> Direct
                  </span>
                  <span className="text-[#B8860B] font-bold">from €38</span>
                </div>
                <Link to="/book?type=transfer">
                  <Button size="sm" variant="outline" className="border-[#0A2540] text-[#B8860B] hover:bg-[#B8860B] hover:text-slate-900">
                    Book
                  </Button>
                </Link>
              </div>
            </div>

            <div className="bg-white border border-slate-200 rounded-2xl p-6 hover:border-[#0A2540]/50 transition-all">
              <h4 className="text-xl font-bold text-slate-900 mb-2">Athinios Port Transfer</h4>
              <p className="text-slate-600 text-sm mb-4">
                Ferry port transfer service to/from Athinios Port. Perfect for cruise ships and ferries. Meet & greet at arrival.
              </p>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4 text-slate-600 text-sm">
                  <span className="flex items-center gap-1">
                    <MapPin className="w-4 h-4" /> Port
                  </span>
                  <span className="text-[#B8860B] font-bold">from €38</span>
                </div>
                <Link to="/book?type=transfer">
                  <Button size="sm" variant="outline" className="border-[#0A2540] text-[#B8860B] hover:bg-[#B8860B] hover:text-slate-900">
                    Book
                  </Button>
                </Link>
              </div>
            </div>
          </div>

          <div className="mt-6 grid md:grid-cols-2 gap-6">
            <div className="bg-white border border-slate-200 rounded-2xl p-6 hover:border-[#0A2540]/50 transition-all">
              <h4 className="text-xl font-bold text-slate-900 mb-2">Full Island Tour</h4>
              <p className="text-slate-600 text-sm mb-4">
                Complete Santorini experience: Oia, Fira, traditional villages, Prophet Elias Monastery, and local lunch. Perfect for first-time visitors.
              </p>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4 text-slate-600 text-sm">
                  <span className="flex items-center gap-1">
                    <Clock className="w-4 h-4" /> 8 hours
                  </span>
                  <span className="text-[#B8860B] font-bold">€330</span>
                </div>
                <Link to="/book?type=tour">
                  <Button size="sm" variant="outline" className="border-[#0A2540] text-[#B8860B] hover:bg-[#B8860B] hover:text-slate-900">
                    Book
                  </Button>
                </Link>
              </div>
            </div>

            <div className="bg-white border border-slate-200 rounded-2xl p-6 hover:border-[#0A2540]/50 transition-all">
              <h4 className="text-xl font-bold text-slate-900 mb-2">Custom Private Tour</h4>
              <p className="text-slate-600 text-sm mb-4">
                Design your perfect Santorini day. Choose your destinations, duration, and activities. Flexible and personalized service.
              </p>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4 text-slate-600 text-sm">
                  <span className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" /> Flexible
                  </span>
                  <span className="text-[#B8860B] font-bold">from €195</span>
                </div>
                <Link to="/book?type=tour">
                  <Button size="sm" variant="outline" className="border-[#0A2540] text-[#B8860B] hover:bg-[#B8860B] hover:text-slate-900">
                    Book
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Activities Section */}
      <section id="activities" className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Tours & Experiences</h2>
            <p className="text-xl text-slate-600">Discover the best of Santorini with our curated experiences</p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {activities.map((activity, index) => (
              <div key={index} className="bg-white border border-slate-200 rounded-2xl p-6 hover:border-[#0A2540]/30 transition-all group cursor-pointer">
                <div className="w-12 h-12 rounded-full bg-[#B8860B]/10 flex items-center justify-center text-[#B8860B] mb-4 group-hover:bg-[#B8860B]/20 transition-colors">
                  {activity.icon}
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">{activity.name}</h3>
                <p className="text-slate-600 text-sm">{activity.desc}</p>
              </div>
            ))}
          </div>
          
          <div className="text-center mt-12">
            <Link to="/book">
              <Button className="h-14 px-10 bg-[#0A2540] hover:bg-[#153554] text-white font-semibold text-lg rounded-full">
                Book Your Experience
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 px-6 bg-white/50">
        <div className="max-w-5xl mx-auto">
          <div className="bg-gradient-to-br from-amber-500/20 to-transparent border border-[#0A2540]/30 rounded-3xl p-12 text-center">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Why Choose Us?</h2>
            <p className="text-xl text-slate-700 mb-8 max-w-2xl mx-auto">
              Local expertise, premium service, and unforgettable experiences in Santorini
            </p>
            
            <div className="grid md:grid-cols-4 gap-8 mb-8">
              <div className="text-center">
                <p className="text-4xl font-bold text-[#B8860B]">24/7</p>
                <p className="text-slate-600">Availability</p>
              </div>
              <div className="text-center">
                <p className="text-4xl font-bold text-[#B8860B]">100%</p>
                <p className="text-slate-600">Licensed</p>
              </div>
              <div className="text-center">
                <p className="text-4xl font-bold text-[#B8860B]">Free</p>
                <p className="text-slate-600">Cancellation</p>
              </div>
              <div className="text-center">
                <p className="text-4xl font-bold text-[#B8860B]">Best</p>
                <p className="text-slate-600">Price Guarantee</p>
              </div>
            </div>
            
            <Link to="/book">
              <Button className="h-14 px-10 bg-white hover:bg-slate-100 text-slate-900 font-semibold text-lg rounded-full">
                Start Booking
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Call to Action before Contact */}
      <section className="py-16 px-6 bg-[#0A2540]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
            Ready to Book Your Experience?
          </h2>
          <p className="text-xl text-slate-900/90 mb-8">
            Contact us directly for instant booking assistance
          </p>
          
          <div className="grid md:grid-cols-2 gap-6 max-w-2xl mx-auto">
            {/* Call Us */}
            <a href="tel:+306974994577" className="group">
              <div className="bg-white hover:bg-slate-50 rounded-2xl p-6 transition-all transform hover:scale-105 shadow-xl">
                <div className="w-16 h-16 rounded-full bg-[#B8860B]/10 flex items-center justify-center mx-auto mb-4 group-hover:bg-[#B8860B]/20 transition-colors">
                  <Phone className="w-8 h-8 text-amber-600" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">Call Us</h3>
                <p className="text-2xl font-bold text-amber-600 mb-1">+30 697 499 4577</p>
                <p className="text-sm text-slate-600">Available 24/7</p>
              </div>
            </a>
            
            {/* Email Us */}
            <a href="mailto:giorgos8956@gmail.com" className="group">
              <div className="bg-white hover:bg-slate-50 rounded-2xl p-6 transition-all transform hover:scale-105 shadow-xl">
                <div className="w-16 h-16 rounded-full bg-[#B8860B]/10 flex items-center justify-center mx-auto mb-4 group-hover:bg-[#B8860B]/20 transition-colors">
                  <Mail className="w-8 h-8 text-amber-600" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">Email Us</h3>
                <p className="text-lg font-semibold text-amber-600 mb-1 break-all">giorgos8956@gmail.com</p>
                <p className="text-sm text-slate-600">Quick response guaranteed</p>
              </div>
            </a>
          </div>
        </div>
      </section>

      {/* Google Reviews Section */}
      <GoogleReviews />

      {/* Contact Section */}
      <section id="contact" className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Contact Us</h2>
            <p className="text-xl text-slate-600">We&apos;re here to help plan your perfect Santorini experience</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8 max-w-3xl mx-auto">
            <div className="text-center">
              <div className="w-14 h-14 rounded-full bg-[#B8860B]/10 flex items-center justify-center text-[#B8860B] mx-auto mb-4">
                <Phone className="w-6 h-6" />
              </div>
              <p className="text-slate-900 font-medium">Call Us</p>
              <a href="tel:+306974994577" className="text-[#B8860B] hover:text-[#C9A96E]">+30 697 499 4577</a>
            </div>
            <div className="text-center">
              <div className="w-14 h-14 rounded-full bg-[#B8860B]/10 flex items-center justify-center text-[#B8860B] mx-auto mb-4">
                <Mail className="w-6 h-6" />
              </div>
              <p className="text-slate-900 font-medium">Email Us</p>
              <a href="mailto:giorgos8956@gmail.com" className="text-[#B8860B] hover:text-[#C9A96E] text-sm break-all">giorgos8956@gmail.com</a>
            </div>
            <div className="text-center">
              <div className="w-14 h-14 rounded-full bg-[#B8860B]/10 flex items-center justify-center text-[#B8860B] mx-auto mb-4">
                <MapPin className="w-6 h-6" />
              </div>
              <p className="text-slate-900 font-medium">Location</p>
              <p className="text-slate-600">Akrotiri, Santorini</p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-slate-200">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_santorini-booking/artifacts/b01x660c_FullLogo%20%281%29.png" 
                alt="Santorini Best Experience"
                className="h-14 md:h-16"
              />
            </div>
            
            <div className="text-center md:text-left">
              <p className="text-slate-900 font-semibold mb-2">Santorini Best Experience</p>
              <p className="text-slate-600 text-sm">Akrotiri, Santorini, Greece</p>
              <p className="text-slate-600 text-sm">Phone: <a href="tel:+306974994577" className="text-[#B8860B] hover:text-[#C9A96E]">+30 697 499 4577</a></p>
              <p className="text-slate-600 text-sm">Email: <a href="mailto:giorgos8956@gmail.com" className="text-[#B8860B] hover:text-[#C9A96E]">giorgos8956@gmail.com</a></p>
            </div>
            
            <div className="flex gap-6 text-sm">
              <Link to="/privacy" className="text-slate-600 hover:text-slate-900 transition-colors">Privacy Policy</Link>
              <Link to="/terms" className="text-slate-600 hover:text-slate-900 transition-colors">Terms of Service</Link>
              <Link to="/cancellation" className="text-slate-600 hover:text-slate-900 transition-colors">Cancellation Policy</Link>
            </div>
          </div>
          
          <div className="border-t border-slate-200 mt-8 pt-6 text-center text-slate-500 text-sm">
            <p>© 2025 Santorini Best Experience. All rights reserved.</p>
            <Link to="/bookings-admin" className="text-slate-700 hover:text-slate-600 text-xs mt-2 inline-block">
              •
            </Link>
          </div>
        </div>
      </footer>
    </div>
    </>
  );
}
